/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Plus, Edit2, Trash2, FileText } from 'lucide-react';
import { C, inp, lbl, fmt } from './teamUtils';

const ENTITY_MAP = {
  'Box Jordaan':  { name: 'FOBS B.V.',        kvk: '88496899', address: 'Bloemgracht 47, 1016 KD Amsterdam',              place_of_work: 'Bloemgracht 47, 1016 KD Amsterdam'         },
  'Box Plantage': { name: 'Box Social B.V.',   kvk: '70252297', address: 'Plantage Middenlaan 30a, 1018 DG Amsterdam',     place_of_work: 'Plantage Middenlaan 30a, 1018 DG Amsterdam'},
  'Sassys':       { name: 'Lekker Mate B.V.',  kvk: '94652147', address: 'Edisonstraat 38 1, 1098 TD Amsterdam',           place_of_work: 'Schaafstraat 21, 1021 KD Amsterdam'        },
  'Prep Kitchen': { name: 'Lekker Mate B.V.',  kvk: '94652147', address: 'Edisonstraat 38 1, 1098 TD Amsterdam',           place_of_work: 'Hoofdweg 236H, 1057 DG Amsterdam'          },
};

const CAO_FUNCTIONS = [
  { label: 'Service Staff (entry)',  value: 'Medewerker Bediening (B.2)' },
  { label: 'Service Staff',          value: 'Medewerker Bediening (B.3)' },
  { label: 'Supervisor',             value: 'Supervisor (B.5.1)'          },
  { label: 'Manager',                value: 'Manager (B.6.1)'             },
  { label: 'Keukenpersoneel',        value: 'Keukenpersoneel (K.1)'       },
  { label: 'Kitchen Staff (entry)',  value: 'Medewerker Keuken (K.2)'     },
  { label: 'Kitchen Staff',          value: 'Medewerker Keuken (K.3)'     },
  { label: 'Commis Chef',            value: 'Commis Chef (K.3.1)'         },
  { label: 'Chef de Partie',         value: 'Chef de Partie (K.4.1)'      },
  { label: 'Sous Chef',              value: 'Sous Chef (K.5.1)'           },
  { label: 'Head Chef',              value: 'Head Chef (K.6.1)'           },
];

function addMonthsToDate(dateStr, months) {
  if (!dateStr || !months) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + parseInt(months));
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function addMonthsToDateISO(dateStr, months) {
  if (!dateStr || !months) return null;
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + parseInt(months));
  return d.toISOString().split('T')[0];
}

function fmtContractDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ── Blank form state ───────────────────────────────────────────────────
function blankForm(member) {
  const isFT = member.employment_type === 'Full Time';
  return {
    contract_type:   member.contract_type || 'Fixed Term',
    employment_type: member.employment_type || 'Part Time',
    start_date:      member.contract_start_date || member.join_date || '',
    duration_months: member.contract_months || '6',
    cao_function:    CAO_FUNCTIONS[0].value,
    probation:       'one (1) month',
    gross_salary:    member.gross_monthly_salary ? String(member.gross_monthly_salary) : '',
    hourly_rate:     member.hourly_rate ? String(member.hourly_rate) : '',
    salary_review:   '',
    notes:           '',
  };
}

// ── Contract history log ───────────────────────────────────────────────
function ContractHistory({ contracts, isAdmin, onEdit, onDelete, loading }) {
  if (loading) return <div style={{ fontSize: '13px', color: C.mid, padding: '8px 0' }}>Loading…</div>;
  if (contracts.length === 0) return (
    <div style={{ fontSize: '13px', color: C.mid, fontStyle: 'italic', padding: '8px 0' }}>
      No contracts on record yet.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {contracts.map(c => (
        <div key={c.id} style={{ borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', background: C.cream }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: C.charcoal }}>
                  Contract #{c.contract_number}
                </span>
                <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                  background: c.contract_type === 'Permanent' ? C.greenLight : C.amberLight,
                  color: c.contract_type === 'Permanent' ? C.green : C.amber }}>
                  {c.contract_type}
                </span>
                <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                  background: C.skyBlueLight, color: C.skyText }}>
                  {c.employment_type}
                </span>
                {c.manually_entered && (
                  <span style={{ fontSize: '10px', color: C.mid, fontStyle: 'italic' }}>manual</span>
                )}
              </div>
              <div style={{ fontSize: '11px', color: C.mid, marginTop: '3px' }}>
                {fmt(c.start_date)} {c.end_date ? `→ ${fmt(c.end_date)}` : '(permanent)'}
                {c.cao_function ? ` · ${c.cao_function}` : ''}
              </div>
              {isAdmin && (c.gross_salary || c.hourly_rate) && (
                <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>
                  {c.gross_salary
                    ? `€${parseFloat(c.gross_salary).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}/mo`
                    : `€${parseFloat(c.hourly_rate).toFixed(2)}/hr`}
                </div>
              )}
              <div style={{ fontSize: '10px', color: C.mid, marginTop: '3px' }}>
                {c.manually_entered
                  ? `Entered manually${c.generated_by ? ` by ${c.generated_by}` : ''}`
                  : `Generated${c.generated_by ? ` by ${c.generated_by}` : ''}`}
                {c.generated_at ? ` · ${fmtDateTime(c.generated_at)}` : ''}
              </div>
              {c.notes && (
                <div style={{ fontSize: '11px', color: C.charcoal, marginTop: '4px', fontStyle: 'italic' }}>{c.notes}</div>
              )}
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => onEdit(c)}
                  style={{ padding: '5px 8px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Edit2 size={13} color={C.mid} />
                </button>
                <button onClick={() => onDelete(c)}
                  style={{ padding: '5px 8px', background: C.white, border: `1px solid ${C.red}44`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Trash2 size={13} color={C.red} />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────
export default function ContractModal({ member, currentUser, onClose, onContractSaved }) {
  const isAdmin  = currentUser?.role?.toLowerCase() === 'admin';
  const isFT     = member.employment_type === 'Full Time';
  const entity   = ENTITY_MAP[member.venue] || {};

  const [view,           setView]          = useState('history'); // 'history' | 'generate' | 'manual' | 'edit'
  const [contracts,      setContracts]     = useState([]);
  const [latestContract, setLatestContract] = useState(null);
  const [loadingHist,    setLoadingHist]   = useState(true);
  const [editTarget,     setEditTarget]    = useState(null);
  const [form,           setForm]          = useState(() => blankForm(member));
  const [generating,     setGenerating]    = useState(false);
  const [saving,         setSaving]        = useState(false);
  const [error,          setError]         = useState('');

  const isFTForm = form.employment_type === 'Full Time';
  const endDate  = form.start_date && form.duration_months && form.contract_type !== 'Permanent'
    ? addMonthsToDate(form.start_date, form.duration_months)
    : '';

  useEffect(() => { loadContracts(); }, [member.id]);

  async function loadContracts() {
    setLoadingHist(true);
    const { data } = await supabase
      .from('contracts')
      .select('*')
      .eq('team_member_id', member.id)
      .order('start_date', { ascending: false });
    setContracts(data || []);
    // Most recent contract by start_date — used to pre-fill salary in forms
    setLatestContract(data && data.length > 0 ? data[0] : null);
    setLoadingHist(false);
  }

  function prefillForm() {
    const base = blankForm(member);
    if (latestContract) {
      if (latestContract.gross_salary) base.gross_salary = String(latestContract.gross_salary);
      if (latestContract.hourly_rate)  base.hourly_rate  = String(latestContract.hourly_rate);
      if (latestContract.cao_function) base.cao_function = latestContract.cao_function;
    }
    return base;
  }

  async function renumberContracts() {
    // Fetch all contracts ordered by start_date ASC, generated_at ASC as tiebreaker
    const { data } = await supabase
      .from('contracts')
      .select('id, start_date, generated_at')
      .eq('team_member_id', member.id)
      .order('start_date', { ascending: true })
      .order('generated_at', { ascending: true });
    if (!data || data.length === 0) return;
    // Update each with its correct sequential number
    await Promise.all(
      data.map((c, i) =>
        supabase.from('contracts').update({ contract_number: i + 1 }).eq('id', c.id)
      )
    );
  }

  async function syncTeamMember(f) {
    // Keep team_members current contract columns in sync
    const updates = {
      contract_type:       f.contract_type,
      contract_start_date: f.start_date,
      contract_end_date:   f.contract_type === 'Permanent' ? null : addMonthsToDateISO(f.start_date, f.duration_months),
      contract_months:     f.contract_type === 'Permanent' ? null : parseInt(f.duration_months),
    };
    await supabase.from('team_members').update(updates).eq('id', member.id);
  }

  async function handleSaveManual() {
    if (!form.start_date) { setError('Start date is required.'); return; }
    if (form.contract_type !== 'Permanent' && !form.duration_months) { setError('Duration is required for fixed term.'); return; }
    setSaving(true); setError('');
    const payload = {
      team_member_id:  member.id,
      contract_number: 0, // will be corrected by renumberContracts
      contract_type:   form.contract_type,
      employment_type: form.employment_type,
      start_date:      form.start_date,
      end_date:        form.contract_type === 'Permanent' ? null : addMonthsToDateISO(form.start_date, form.duration_months),
      duration_months: form.contract_type === 'Permanent' ? null : parseInt(form.duration_months),
      contract_hours:  member.contract_hours || null,
      cao_function:    form.cao_function,
      gross_salary:    isFTForm && form.gross_salary ? parseFloat(form.gross_salary) : null,
      hourly_rate:     !isFTForm && form.hourly_rate ? parseFloat(form.hourly_rate) : null,
      probation:       form.probation,
      generated_by:    currentUser?.name || 'Admin',
      generated_at:    new Date().toISOString(),
      manually_entered: true,
      notes:           form.notes || null,
    };
    let dbErr;
    if (editTarget) {
      ({ error: dbErr } = await supabase.from('contracts').update(payload).eq('id', editTarget.id));
    } else {
      ({ error: dbErr } = await supabase.from('contracts').insert(payload));
      if (!dbErr) await syncTeamMember(form);
    }
    if (dbErr) { setSaving(false); setError('Error saving: ' + dbErr.message); return; }
    await renumberContracts();
    await loadContracts();
    setSaving(false);
    if (onContractSaved) onContractSaved();
    setView('history'); setEditTarget(null);
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete Contract #${c.contract_number}? This cannot be undone.`)) return;
    await supabase.from('contracts').delete().eq('id', c.id);
    await renumberContracts();
    await loadContracts();
    if (onContractSaved) onContractSaved();
  }

  function handleEdit(c) {
    setEditTarget(c);
    setForm({
      contract_type:   c.contract_type,
      employment_type: c.employment_type,
      start_date:      c.start_date || '',
      duration_months: c.duration_months ? String(c.duration_months) : '6',
      cao_function:    c.cao_function || CAO_FUNCTIONS[0].value,
      probation:       c.probation || 'one (1) month',
      gross_salary:    c.gross_salary ? String(c.gross_salary) : '',
      hourly_rate:     c.hourly_rate  ? String(c.hourly_rate)  : '',
      salary_review:   '',
      notes:           c.notes || '',
    });
    setView('edit');
  }

  async function handleGenerate() {
    setError('');
    if (!form.start_date)                   { setError('Contract start date is required.'); return; }
    if (!form.duration_months && form.contract_type !== 'Permanent') { setError('Duration is required.'); return; }
    if (isFTForm && !form.gross_salary)     { setError('Gross monthly salary is required.'); return; }
    if (!isFTForm && !form.hourly_rate)     { setError('Hourly rate is required.'); return; }
    if (!member.bsn)                        { setError('BSN is missing from the TM card.'); return; }
    if (!employeeAddress)                   { setError('Address is missing from the TM card.'); return; }

    setGenerating(true);
    try {
      // We don't know the final number yet — renumberContracts will set it after insert.
      // Use a placeholder number for the filename; history view will show the correct number.
      const currentCount = contracts.length;
      const estimatedNumber = currentCount + 1;
      const payload = {
        contract_type:            isFTForm ? 'ft' : 'pt',
        entity_name:              entity.name,
        entity_address:           entity.address,
        entity_kvk:               entity.kvk,
        venue_name:               member.venue,
        place_of_work:            entity.place_of_work,
        employee_name:            member.name,
        employee_dob:             member.birthday ? fmtContractDate(member.birthday) : '___________',
        employee_address:         employeeAddress,
        employee_bsn:             member.bsn,
        contract_duration_months: form.duration_months,
        contract_start_date:      fmtContractDate(form.start_date),
        contract_end_date:        endDate,
        employee_role:            member.role || '___________',
        cao_reference_function:   form.cao_function,
        probation_period:         form.probation,
        ...(isFTForm ? {
          contract_hours:       member.contract_hours || '___',
          gross_monthly_salary: form.gross_salary,
          salary_review_note:   form.salary_review,
        } : {
          hourly_rate: form.hourly_rate,
        }),
      };

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `https://unvzfprqkmxrifwodlec.supabase.co/functions/v1/generate-contract`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Contract_${member.name.replace(/[^a-zA-Z0-9]/g, '_')}_#${estimatedNumber}_${form.start_date}.docx`;
      a.click();
      URL.revokeObjectURL(url);

      // Save to contracts table — contract_number corrected by renumberContracts below
      const record = {
        team_member_id:  member.id,
        contract_number: 0,
        contract_type:   form.contract_type,
        employment_type: form.employment_type,
        start_date:      form.start_date,
        end_date:        form.contract_type === 'Permanent' ? null : addMonthsToDateISO(form.start_date, form.duration_months),
        duration_months: form.contract_type === 'Permanent' ? null : parseInt(form.duration_months),
        contract_hours:  member.contract_hours || null,
        cao_function:    form.cao_function,
        gross_salary:    isFTForm && form.gross_salary ? parseFloat(form.gross_salary) : null,
        hourly_rate:     !isFTForm && form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        probation:       form.probation,
        generated_by:    currentUser?.name || 'Admin',
        generated_at:    new Date().toISOString(),
        manually_entered: false,
        notes:           form.notes || null,
      };
      await supabase.from('contracts').insert(record);
      await syncTeamMember(form);
      await renumberContracts();
      await loadContracts();
      if (onContractSaved) onContractSaved();
      setView('history');
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  const employeeAddress = [member.street, member.zip, member.city].filter(Boolean).join(', ');
  const missingFields   = [];
  if (!member.bsn)      missingFields.push('BSN');
  if (!member.birthday) missingFields.push('Date of birth');
  if (!employeeAddress) missingFields.push('Address');

  // ── Form fields (shared between generate + manual + edit) ────────────
  function FormFields({ showGenerate }) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={lbl}>Contract Type</label>
            <select value={form.contract_type} onChange={e => setForm(p => ({ ...p, contract_type: e.target.value }))} style={inp}>
              <option value="Fixed Term">Fixed Term</option>
              <option value="Permanent">Permanent</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Employment Type</label>
            <select value={form.employment_type} onChange={e => setForm(p => ({ ...p, employment_type: e.target.value }))} style={inp}>
              <option value="Part Time">Part Time</option>
              <option value="Full Time">Full Time</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Start Date</label>
            <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} style={inp}/>
          </div>
          {form.contract_type !== 'Permanent' && (
            <div>
              <label style={lbl}>Duration (months)</label>
              <input type="number" min="1" max="36" value={form.duration_months} onChange={e => setForm(p => ({ ...p, duration_months: e.target.value }))} style={inp}/>
            </div>
          )}
          {endDate && form.contract_type !== 'Permanent' && (
            <div style={{ gridColumn: 'span 2' }}>
              <label style={lbl}>End Date (calculated)</label>
              <div style={{ padding: '8px 10px', background: C.skyBlueLight, borderRadius: '8px', fontSize: '13px', color: C.skyText, fontWeight: 600, border: `1px solid ${C.skyBlue}` }}>{endDate}</div>
            </div>
          )}
          <div>
            <label style={lbl}>Probation</label>
            <select value={form.probation} onChange={e => setForm(p => ({ ...p, probation: e.target.value }))} style={inp}>
              <option value="no probationary">None</option>
              <option value="one (1) month">1 month</option>
              <option value="two (2) month">2 months</option>
            </select>
          </div>
          <div>
            <label style={lbl}>CAO Function</label>
            <select value={form.cao_function} onChange={e => setForm(p => ({ ...p, cao_function: e.target.value }))} style={inp}>
              {CAO_FUNCTIONS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          {isFTForm ? (
            <>
              <div>
                <label style={lbl}>Gross Monthly Salary (€)</label>
                <input type="text" value={form.gross_salary} onChange={e => setForm(p => ({ ...p, gross_salary: e.target.value }))} placeholder="€2,500.00" style={inp}/>
              </div>
              {showGenerate && (
                <div>
                  <label style={lbl}>Salary Review Note <span style={{ fontWeight: 400, color: C.mid }}>(optional)</span></label>
                  <input type="text" value={form.salary_review} onChange={e => setForm(p => ({ ...p, salary_review: e.target.value }))} placeholder="e.g. Review after 3 months" style={inp}/>
                </div>
              )}
            </>
          ) : (
            <div>
              <label style={lbl}>Gross Hourly Rate (€)</label>
              <input type="text" value={form.hourly_rate} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} placeholder="€14.71" style={inp}/>
            </div>
          )}
        </div>
        <div>
          <label style={lbl}>Notes <span style={{ fontWeight: 400, color: C.mid }}>(optional)</span></label>
          <input type="text" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Renewal after parental leave" style={inp}/>
        </div>
      </div>
    );
  }

  // ── View: History ────────────────────────────────────────────────────
  if (view === 'history') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
        <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '18px 24px', background: C.navy, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>Contracts</div>
              <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>{member.name} · {member.venue}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {isAdmin && (
                <>
                  <button onClick={() => { setForm(prefillForm()); setEditTarget(null); setError(''); setView('generate'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', color: C.white, fontSize: '12px', fontWeight: 600 }}>
                    <FileText size={13}/>Generate
                  </button>
                  <button onClick={() => { setForm(prefillForm()); setEditTarget(null); setError(''); setView('manual'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', color: C.white, fontSize: '12px', fontWeight: 600 }}>
                    <Plus size={13}/>Log manually
                  </button>
                </>
              )}
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}><X size={18}/></button>
            </div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <ContractHistory
              contracts={contracts}
              isAdmin={isAdmin}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loadingHist}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── View: Generate ───────────────────────────────────────────────────
  if (view === 'generate') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setView('history')}>
        <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '18px 24px', background: C.navy, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>Generate Contract</div>
              <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>{member.name} · {isFT ? 'Full Time' : 'Part Time'}</div>
            </div>
            <button onClick={() => setView('history')} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}><X size={18}/></button>
          </div>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {missingFields.length > 0 && (
              <div style={{ background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '12px 14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.amber, marginBottom: '4px' }}>⚠ Missing from TM card</div>
                <div style={{ fontSize: '12px', color: C.charcoal }}>{missingFields.join(' · ')} — will appear as blanks. Fill in Word before uploading to SignEasy.</div>
              </div>
            )}

            {/* Auto-filled preview */}
            <div style={{ background: C.cream, borderRadius: '10px', padding: '14px 16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Auto-filled from TM card</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  ['Entity',        entity.name || '—'],
                  ['KvK',           entity.kvk  || '—'],
                  ['Venue address', entity.address || '—'],
                  ['Place of work', entity.place_of_work || '—'],
                  ['Employee',      member.name],
                  ['DOB',           member.birthday ? fmtContractDate(member.birthday) : <span style={{ color: C.amber }}>missing</span>],
                  ['BSN',           member.bsn ? '●●●●●●●●●' : <span style={{ color: C.amber }}>missing</span>],
                  ['Address',       employeeAddress || <span style={{ color: C.amber }}>missing</span>],
                  ['Hours/wk',      isFT ? `${member.contract_hours || '—'}hr` : '0-hour'],
                ].map(([l, v]) => (
                  <div key={l} style={{ gridColumn: ['Venue address','Place of work','Address'].includes(l) ? 'span 2' : 'auto' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', marginBottom: '2px' }}>{l}</div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: C.charcoal }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <FormFields showGenerate={true}/>

            {error && <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.red }}>{error}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleGenerate} disabled={generating}
                style={{ flex: 1, padding: '10px', background: generating ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: generating ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                {generating ? 'Generating…' : '⬇ Generate & Download'}
              </button>
              <button onClick={() => setView('history')} style={{ padding: '10px 16px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontSize: '13px', color: C.mid }}>Cancel</button>
            </div>
            <div style={{ fontSize: '11px', color: C.mid, textAlign: 'center' }}>Downloads as .docx — upload to SignEasy to add signatures</div>
          </div>
        </div>
      </div>
    );
  }

  // ── View: Manual entry / Edit ────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setView('history')}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 24px', background: C.navy, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>
              {view === 'edit' ? `Edit Contract #${editTarget?.contract_number}` : 'Log Contract Manually'}
            </div>
            <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>{member.name}</div>
          </div>
          <button onClick={() => setView('history')} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}><X size={18}/></button>
        </div>
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FormFields showGenerate={false}/>
          {error && <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.red }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSaveManual} disabled={saving}
              style={{ flex: 1, padding: '10px', background: saving ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}>
              {saving ? 'Saving…' : view === 'edit' ? 'Save Changes' : 'Log Contract'}
            </button>
            <button onClick={() => setView('history')} style={{ padding: '10px 16px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontSize: '13px', color: C.mid }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}